<template>
  <v-container class="bg-white">
    <v-btn
      prepend-icon="mdi-arrow-left"
      @click="router.back()"
      >Zurück</v-btn
    >
    <v-card
      class="pa-5"
      variant="text">
      <v-card-title>Eigene Lizenz bearbeiten</v-card-title>
      <v-card-text>
        <v-row v-if="paymentInformation.brand">
          <v-col cols="6"> Zahlungsart </v-col>
          <v-col cols="6" data-cy="payment-brand"> {{ paymentInformation.brand }} </v-col>
          <v-col cols="6"> Letzte 4 Ziffern </v-col>
          <v-col cols="6" data-cy="payment-last4"> {{ paymentInformation.last4 }} </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
      </v-card-actions>
    </v-card>
    <v-divider></v-divider>
    <!-- null = never paid / abandoned checkout: modules stay selectable -->
    <template v-if="current_status === 'unpaid'">
      <v-alert
        data-cy="licence-not-paid"
        color="error"
        :dismissible="false">
        <span>{{ $t("views.licence.other.not-paid") }}</span>
        <v-spacer></v-spacer>
        <v-btn
          style="color: black"
          data-cy="licence-pay-now"
          v-if="current_status !== 'draft'"
          @click="payNow">
          {{ $t("views.licence.other.pay-now") }}
        </v-btn>
      </v-alert>
    </template>
    <v-alert
      v-if="showTrialPaymentHint"
      type="warning"
      class="mt-4"
      data-cy="licence-trial-payment-hint">
      <span>{{ $t("views.licence.other.trialPaymentHint", { date: trialEndDate }) }}</span>
      <v-btn
        class="ml-4"
        data-cy="licence-add-payment-method"
        @click="addPaymentMethod">
        {{ $t("views.licence.other.addPaymentMethod") }}
      </v-btn>
    </v-alert>
    <v-alert
      v-if="pendingSwitch"
      type="info"
      class="mt-4"
      data-cy="licence-pending-mode-switch">
      {{
        $t(
          "views.licence.other.pendingSwitchInfo" +
            (next_phase_mode === 'yearly' ? 'Yearly' : 'Monthly'),
          {
            date: pendingDate,
            currentCosts: pendingCosts.current,
            newCosts: pendingCosts.next
          }
        )
      }}
    </v-alert>
    <PromoBox
      :promo="promo"
      :trials="trials"
      :active-discount="activeDiscount" />
    <div :class="current_status === 'unpaid' ? 'disabled-change-module' : ''">
      <ModuleListPrivate
        ref="moduleList"
        v-if="loaded"
        :model-value="modules"
        :current_phase="current_phase"
        :current_phase_mode="current_phase_mode"
        :next_phase="next_phase"
        :next_phase_mode="next_phase_mode"
        :save="updateModules"
        :saving="saving"
        :current_status="current_status"
        :promo-code="promo.appliedCode.value"
        :promo-info="promo.promoInfo.value"
        :active-discount="activeDiscount"
        :trials="trials"
        :licenceId="licence_id" v-model:mode="paymentMode" :next_billing_cycle_anchor="next_billing_cycle_anchor" />
    </div>
    <v-dialog v-model="addressDialog">
      <InvoiceAdressView :success="finalizeUpdate" />
    </v-dialog>
  </v-container>
</template>

<script lang="ts" setup>
import { useSpaceStore } from "@/stores/spaceStore";
import axios from "axios";
import { useTranslation } from "i18next-vue";
import { onMounted, onUnmounted, useTemplateRef } from "vue";
import { computed } from "vue";
import { ref } from "vue";
import { useConfirm, useSnackbar } from "vuetify-use-dialog";
import { SPACE_RIGHT_WRITE, spaceRightNames } from "@/utils/constants";
import { useMainStore } from "@/stores/mainStore";
import { useAuthStore } from "@/stores/authStore";
import { SPACE_RIGHT_READ, MODULES } from "@/utils/constants";
import ModuleListPrivate from "./Licence/ModuleListPrivate.vue";
import { getPhaseCosts, usePromo } from "./Licence/moduleComposable";
import PromoBox from "./Licence/PromoBox.vue";
import { useRouter } from "vue-router";
import InvoiceAdressView from "@/components/dialogs/InvoiceAdressView.vue";
import { useSocketStore } from "@/stores/socketStore";

const mainStore = useMainStore();
const authStore = useAuthStore();
const socketStore = useSocketStore();

const moduleList = useTemplateRef("moduleList");

const router = useRouter();

const { t } = useTranslation();
const createConfirm = useConfirm();
const createSnackbar = useSnackbar();

const addressDialog = ref(false);

const emits = defineEmits(["close"]);

const modules = ref({});
const current_status = ref("paid");
const licence_id = ref();
const loaded = ref(false);
const paymentMode = ref("monthly");

const next_phase = ref({});
const next_phase_mode = ref("");
const current_phase = ref({});
const current_phase_mode = ref("");

const next_billing_cycle_anchor = ref<null|number>(null)

const paymentInformation = ref({
  last4: "",
  brand: ""
});
// from payment-information; has_payment_method is explicit because card is null for SEPA
const subscriptionInfo = ref<any>(null);
const showTrialPaymentHint = computed(
  () =>
    subscriptionInfo.value?.status === "trialing" &&
    !subscriptionInfo.value.has_payment_method &&
    !subscriptionInfo.value.cancel_scheduled
);
const trialEndDate = computed(() =>
  new Date((subscriptionInfo.value?.trial_end ?? 0) * 1000).toLocaleDateString()
);

const saving = ref(false);

// active module trials and price discount of the licence (display only)
const trials = ref<{ module: string; quantity: number; end_date: string }[]>([]);
const activeDiscount = ref<any>(null);

// a redeemed module trial reloads the licence
const promo = usePromo(licence_id, () => loadModules());

const pendingSwitch = computed(
  () =>
    !!next_billing_cycle_anchor.value &&
    current_phase_mode.value !== next_phase_mode.value
);
const pendingDate = computed(() =>
  new Date((next_billing_cycle_anchor.value ?? 0) * 1000).toLocaleDateString()
);
const pendingCosts = computed(() => ({
  current: getPhaseCosts(
    current_phase.value,
    current_phase_mode.value,
    mainStore.modules
  ),
  next: getPhaseCosts(next_phase.value, next_phase_mode.value, mainStore.modules)
}));

// The user changed the mode or the modules and didn't save yet
const hasUnsavedChanges = () =>
  paymentMode.value !== next_phase_mode.value ||
  JSON.stringify(modules.value) !== JSON.stringify(next_phase.value);

function onPaymentChange() {
  console.log("payment-change Licence Private");
  // a late webhook must not discard the unsaved selection
  if (loaded.value && hasUnsavedChanges()) return;
  loadModules();
}

socketStore.socket.on("payment-change", onPaymentChange);

onMounted(() => {
  loadModules();
});

onUnmounted(() => {
  socketStore.socket.off("payment-change", onPaymentChange);
});

function loadModules() {
  loaded.value = false;
  axios.get("/api/licence/private").then((response) => {
    console.log(response.data);

    // modules.value = formatModules(response?.data?.modules);
    // allModules.value = formatModules(response?.data?.allModules);
    next_phase.value = formatModules(response?.data?.next_phase);
    next_phase_mode.value = response?.data?.next_phase_mode;
    modules.value = formatModules(response?.data?.next_phase);
    current_phase.value = formatModules(response?.data?.current_phase);
    current_phase_mode.value = response?.data?.current_phase_mode;
    current_status.value = response?.data?.current_status;
    licence_id.value = response?.data?.licence_id;
    next_billing_cycle_anchor.value = response.data.next_billing_cycle_anchor
    trials.value = response.data.trials ?? [];
    activeDiscount.value = response.data.promotion ?? null;
    // newModules.value = formatModules(response?.data?.newModules);
    loaded.value = true;
    licence_id.value && loadPaymentInformation();
    paymentMode.value = next_phase_mode.value;
  });
}

function loadPaymentInformation() {
  axios
    .get("/api/stripe/payment-information/" + licence_id.value)
    .then((response) => {
      console.log(response.data);
      paymentInformation.value = response.data.card ?? { last4: "", brand: "" };
      subscriptionInfo.value = response.data;
    })
    .catch((err) => console.log(err));
}

function addPaymentMethod() {
  axios
    .get("/api/stripe/customer-portal", { params: { return: "licence" } })
    .then((response) => {
      window.location.href = response.data;
    })
    .catch((err) => {
      console.error(err);
      createSnackbar({
        text: t(
          err.response?.status === 409
            ? "views.licence.other.noPaymentCustomer"
            : "actions.error-message"
        ),
        showCloseButton: false,
        snackbarProps: { color: "error", timeout: 2000 }
      });
    });
}

function formatModules(
  modules: Record<string, number>
): Record<number, number> {
  console.log("Hier", modules);
  return Object.fromEntries(
    mainStore.modules.map((module) => [module.value, modules[module.name]])
  );
}

const byName = computed(() => {
  return Object.fromEntries(
    mainStore.modules.map((module) => [
      module.name,
      modules.value[module.value]
    ])
  );
});

function updateModules() {
  if (!authStore.user.stripe) {
    addressDialog.value = true;
  } else {
    finalizeUpdate();
  }
}

function finalizeUpdate() {
  addressDialog.value = false;
  saving.value = true;
  axios
    .post("/api/licence/private", {
      modules: byName.value,
      paymentMode: paymentMode.value,
      promoCode: promo.appliedCode.value
    })
    .then((x) => {
      console.log(x);
      // mainStore.loadCalculationSpaces();
      // router.back();
      saving.value = false;
      router.push(x.data || { name: "profile" });
    })
    .catch((x) => {
      saving.value = false;
      console.log(x);
      const error = x?.response?.data;
      if (error?.error) {
        promo.promoError.value = promo.errorText(error.error, error.data);
      }
      promo.showApiError(x);
    });
}

function payNow() {
  if (current_status.value === null) {
    moduleList.value.saveModules(updateModules);
  } else {
    axios
      .get("/api/stripe/latest-invoice/" + licence_id.value)
      .then((response) => {
        router.push({
          name: "payment-invoice",
          params: { licence_id: licence_id.value, invoice_id: response.data.id }
        });
      })
      .catch((err) => {
        console.error(err);
        createSnackbar({
          text: t("actions.error-message"),
          showCloseButton: false,
          snackbarProps: { color: "error", timeout: 2000 }
        });
      });
  }
}
</script>

<style>
.disabled-change-module {
  opacity: 0.7;
  pointer-events: none;
}
</style>
